import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { logError } from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!token) { setError('Missing reset token. Open the link from your email again.'); return; }
    if (newPassword.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (newPassword !== confirm) { setError('Passwords do not match.'); return; }

    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: newPassword }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || 'Could not reset password. The link may be expired.');
      } else {
        setSuccess(true);
        setTimeout(() => navigate('/admin/login'), 2000);
      }
    } catch (err) {
      logError('Admin reset-password error:', err);
      setError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
      <Card style={{ maxWidth: 480, width: '100%', backgroundColor: '#1e293b', border: '1px solid #334155' }} data-testid="admin-reset-page">
        <CardHeader style={{ textAlign: 'center' }}>
          <Lock size={40} color="#10b981" style={{ margin: '0 auto 0.75rem' }} />
          <CardTitle style={{ color: '#f1f5f9', fontSize: '1.5rem' }}>Set a new admin password</CardTitle>
        </CardHeader>
        <CardContent>
          {success ? (
            <div data-testid="admin-reset-success" style={{
              padding: '1rem', backgroundColor: '#064e3b', border: '1px solid #047857',
              borderRadius: '6px', color: '#a7f3d0', textAlign: 'center', fontSize: '0.875rem',
            }}>
              Password updated. Redirecting to login…
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '1rem' }}>
              <div>
                <Label style={{ color: '#cbd5e1' }} htmlFor="new-pw">New password</Label>
                <Input
                  id="new-pw"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  data-testid="admin-reset-new"
                  style={{ marginTop: '0.5rem', backgroundColor: '#0f172a', border: '1px solid #334155', color: '#f1f5f9' }}
                />
              </div>
              <div>
                <Label style={{ color: '#cbd5e1' }} htmlFor="confirm-pw">Confirm password</Label>
                <Input
                  id="confirm-pw"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  data-testid="admin-reset-confirm"
                  style={{ marginTop: '0.5rem', backgroundColor: '#0f172a', border: '1px solid #334155', color: '#f1f5f9' }}
                />
              </div>
              {error && (
                <div data-testid="admin-reset-error" style={{
                  padding: '0.75rem', backgroundColor: '#7f1d1d', border: '1px solid #991b1b',
                  borderRadius: '6px', color: '#fecaca', fontSize: '0.875rem',
                }}>{error}</div>
              )}
              <Button
                type="submit"
                disabled={isSubmitting}
                data-testid="admin-reset-submit"
                style={{ backgroundColor: '#10b981', color: 'white', fontWeight: 600 }}
              >
                {isSubmitting ? 'Saving…' : 'Set new password'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminResetPasswordPage;
